'use client'

import Link from 'next/link'
import { formatDate } from '@/lib/utils/format'
import type { BlogPost } from '@/lib/blog/types'

interface ArticleCardProps {
  post: BlogPost
  onTagClick?: (tag: string) => void
}

export function ArticleCard({ post, onTagClick }: ArticleCardProps) {
  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group block rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-6 shadow-[var(--card-shadow)] transition-all duration-200 hover:-translate-y-1 hover:border-[var(--color-brand-indigo)] hover:shadow-md"
    >
      {/* Meta */}
      <div className="mb-3 flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
        <time dateTime={post.published}>
          {formatDate(post.published)}
        </time>
        <span aria-hidden="true">&middot;</span>
        <span>{post.readTime}</span>
      </div>

      {/* Title */}
      <h2 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)] group-hover:text-[var(--color-brand-indigo)] transition-colors line-clamp-2">
        {post.title}
      </h2>

      {/* Excerpt */}
      <p className="mb-4 text-sm text-[var(--color-text-secondary)] line-clamp-3">
        {post.excerpt}
      </p>

      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {post.tags.map((tag) => (
          <button
            key={tag}
            type="button"
            onClick={(e) => {
              e.preventDefault()
              onTagClick?.(tag)
            }}
            className="rounded-full bg-[var(--tag-bg)] px-2.5 py-0.5 text-xs font-medium text-[var(--tag-text)] transition-opacity hover:opacity-80"
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Author */}
      <div className="mt-4 text-xs text-[var(--color-text-muted)]">
        By {post.author}
      </div>
    </Link>
  )
}
